import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import DatePickerAdvance from "@/components/DatePicker";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Download, 
  TrendingUp, 
  DollarSign, 
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DatePicker = DatePickerAdvance;

type DateRange = {
  from?: Date;
  to?: Date;
};

interface StatData {
  amount: number;
  count: number;
  repaid?: number;
}

export default function LoansReport() {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<DateRange | undefined>(undefined);
  const [endDate, setEndDate] = useState<DateRange | undefined>(undefined);
  const [selectedGroup, setSelectedGroup] = useState<string>("all");
  const [selectedBranch, setSelectedBranch] = useState<string>("all");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // Fetch groups for filter
  const { data: groups = [] } = useQuery(["groups"], async () => {
    const { data } = await api.get("/groups");
    return data.results || [];
  });

  // Fetch branches for filter
  const { data: branches = [] } = useQuery(["branches"], async () => {
    const { data } = await api.get("/branches");
    return data.results || [];
  });

  // Fetch districts for filter
  const { data: districts = [] } = useQuery(["districts"], async () => {
    const { data } = await api.get("/districts");
    return data.results || [];
  });

  // Fetch loans with filters
  const loansQuery = useQuery({
    queryKey: ["loans-report", { startDate, endDate, selectedGroup, selectedBranch, selectedDistrict, selectedStatus }],
    queryFn: async () => {
      const filters: any[] = [];

      if (selectedGroup !== "all") {
        filters.push({
          field: "groupId",
          operator: "eq",
          value: Number(selectedGroup),
        });
      }

      if (selectedBranch !== "all") {
        filters.push({
          field: "group.branch.name",
          operator: "eq",
          value: selectedBranch,
        });
      }

      if (selectedDistrict !== "all") {
        filters.push({
          field: "group.branch.district.name",
          operator: "eq",
          value: selectedDistrict,
        });
      }

      if (selectedStatus !== "all") {
        filters.push({
          field: "status",
          operator: "eq",
          value: selectedStatus,
        });
      }

      if (startDate?.from) {
        filters.push({
          field: "createdAt",
          operator: "gte",
          value: format(startDate.from, "yyyy-MM-dd"),
        });
      }

      if (endDate?.to) {
        filters.push({
          field: "createdAt",
          operator: "lte",
          value: format(endDate.to, "yyyy-MM-dd"),
        });
      }

      const { data } = await api.get("/loans", {
        params: {
          page_size: 1000,
          page: 1,
          filters: filters.length > 0 ? filters : undefined,
        },
      });

      return data.results || [];
    },
  });

  const loans = loansQuery.data || [];

  // Calculate statistics
  const stats = {
    totalLoans: loans.length,
    totalDisbursed: loans.reduce((sum, l) => sum + (Number(l.amount) || 0), 0),
    totalRepaid: loans.reduce((sum, l) => sum + (Number(l.paidAmount) || 0), 0),
    totalOutstanding: loans.reduce((sum, l) => sum + (Number(l.outstandingBalance) || 0), 0),
    pendingLoans: loans.filter(l => l.status === "PENDING").length,
    approvedLoans: loans.filter(l => l.status === "APPROVED").length,
    rejectedLoans: loans.filter(l => l.status === "REJECTED").length,
    completedLoans: loans.filter(l => l.status === "COMPLETED" || Number(l.outstandingBalance) === 0).length,
    uniqueMembers: new Set(loans.map(l => l.member?.id).filter(Boolean)).size,
  };

  stats.totalDisbursed = Math.round(stats.totalDisbursed);
  stats.totalRepaid = Math.round(stats.totalRepaid);
  stats.totalOutstanding = Math.round(stats.totalOutstanding);

  const repaymentRate = stats.totalDisbursed > 0 
    ? ((stats.totalRepaid / stats.totalDisbursed) * 100).toFixed(1) 
    : "0.0";

  // Group by status
  const statusStats: Record<string, StatData> = loans.reduce((acc, l) => {
    const status = l.status || "UNKNOWN";
    if (!acc[status]) {
      acc[status] = { count: 0, amount: 0 };
    }
    acc[status].count++;
    acc[status].amount += Number(l.amount) || 0;
    return acc;
  }, {} as Record<string, StatData>);

  // Group by group
  const groupStats: Record<string, StatData> = loans.reduce((acc, l) => {
    const groupName = l.group?.name || "Unknown";
    if (!acc[groupName]) {
      acc[groupName] = { count: 0, amount: 0, repaid: 0 };
    }
    acc[groupName].count++;
    acc[groupName].amount += Number(l.amount) || 0;
    acc[groupName].repaid = (acc[groupName].repaid || 0) + (Number(l.paidAmount) || 0);
    return acc;
  }, {} as Record<string, StatData>);

  // Export to Excel
  const exportToExcel = () => {
    try {
      if (loans.length === 0) {
        toast.error("No data to export");
        return;
      }

      const data = loans.map((l) => ({
        Date: l.createdAt ? format(new Date(l.createdAt), "yyyy-MM-dd HH:mm") : "",
        Member: l.member?.fullNames || "",
        Group: l.group?.name || "",
        Branch: l.group?.branch?.name || "",
        District: l.group?.branch?.district?.name || "",
        "Loan Amount": Number(l.amount) || 0,
        "Interest Rate": l.interestRate || 0,
        "Total Payable": Number(l.totalPayableAmount) || 0,
        "Amount Paid": Number(l.paidAmount) || 0,
        "Outstanding Balance": Number(l.outstandingBalance) || 0,
        Status: l.status || "",
        "Loan Category": l.loanCategory?.name || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Loans");

      const filename = `loans-report-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success("Excel file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export Excel: " + (error.message || "Unknown error"));
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      if (loans.length === 0) {
        toast.error("No data to export");
        return;
      }

      const doc = new jsPDF('landscape');
      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      // Header
      doc.setFontSize(16);
      doc.text(`Loans Report - ${groupName}`, 14, 10);
      doc.setFontSize(12);
      if (startDate?.from && endDate?.to) {
        doc.text(
          `${format(startDate.from, "MMM dd, yyyy")} - ${format(endDate.to, "MMM dd, yyyy")}`,
          14,
          18
        );
      }
      doc.text(`Generated: ${format(new Date(), "MMM dd, yyyy")}`, 14, 24);

      // Summary
      doc.setFontSize(10);
      doc.text(`Total Loans: ${stats.totalLoans}`, 14, 32);
      doc.text(`Total Disbursed: ${stats.totalDisbursed.toLocaleString()} RWF`, 14, 38);
      doc.text(`Total Repaid: ${stats.totalRepaid.toLocaleString()} RWF`, 14, 44);
      doc.text(`Outstanding: ${stats.totalOutstanding.toLocaleString()} RWF`, 14, 50);

      // Prepare table data
      const tableData = loans.map((l) => [
        l.createdAt ? format(new Date(l.createdAt), "MMM dd, yyyy") : "",
        l.member?.fullNames || "",
        l.group?.name || "",
        (Number(l.amount) || 0).toLocaleString(),
        `${l.interestRate || 0}%`,
        (Number(l.totalPayableAmount) || 0).toLocaleString(),
        (Number(l.paidAmount) || 0).toLocaleString(),
        (Number(l.outstandingBalance) || 0).toLocaleString(),
        l.status || "",
      ]);

      const headers = [
        "Date",
        "Member",
        "Group",
        "Loan Amount",
        "Interest",
        "Total Payable",
        "Paid",
        "Outstanding",
        "Status",
      ];

      // Add table
      autoTable(doc as any, {
        head: [headers],
        body: tableData,
        startY: 55,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
      });

      const filename = `loans-report-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);
      toast.success("PDF file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export PDF: " + (error.message || "Unknown error"));
    }
  };

  // Print report
  const handlePrint = () => {
    try {
      if (loans.length === 0) {
        toast.error("No data to print");
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow popups to print the report");
        return;
      }

      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      const dateRangeStr = startDate?.from && endDate?.to
        ? format(startDate.from, "MMM dd, yyyy") + " - " + format(endDate.to, "MMM dd, yyyy")
        : "All Time";

      const generatedDate = format(new Date(), "MMM dd, yyyy");
      const generatedTime = format(new Date(), "HH:mm:ss");
      
      const tableRows = loans.map((l) => {
        const createdAt = l.createdAt ? format(new Date(l.createdAt), "MMM dd, yyyy") : "—";
        const memberName = l.member?.fullNames || "—";
        const groupName = l.group?.name || "—";
        const amount = (Number(l.amount) || 0).toLocaleString();
        const interest = l.interestRate || 0;
        const totalPayable = (Number(l.totalPayableAmount) || 0).toLocaleString();
        const paid = (Number(l.paidAmount) || 0).toLocaleString();
        const outstanding = (Number(l.outstandingBalance) || 0).toLocaleString();
        const status = l.status || "—";
        return "<tr>" +
          "<td>" + createdAt + "</td>" +
          "<td>" + memberName + "</td>" +
          "<td>" + groupName + "</td>" +
          "<td class=\"text-right\">" + amount + "</td>" +
          "<td class=\"text-right\">" + interest + "%</td>" +
          "<td class=\"text-right\">" + totalPayable + "</td>" +
          "<td class=\"text-right\">" + paid + "</td>" +
          "<td class=\"text-right\">" + outstanding + "</td>" +
          "<td>" + status + "</td>" +
          "</tr>";
      }).join('');

      const htmlContent = "<!DOCTYPE html>" +
        "<html>" +
        "<head>" +
        "<title>Loans Report - " + groupName + "</title>" +
        "<style>" +
        "@media print { @page { size: landscape; margin: 1cm; } body { margin: 0; padding: 0; } }" +
        "body { font-family: Arial, sans-serif; padding: 20px; }" +
        ".header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }" +
        ".header h1 { margin: 0; font-size: 24px; color: #333; }" +
        ".header h2 { margin: 5px 0; font-size: 18px; color: #666; }" +
        ".summary { display: flex; justify-content: space-around; margin: 20px 0; padding: 15px; background-color: #f5f5f5; border-radius: 5px; }" +
        ".summary-item { text-align: center; }" +
        ".summary-item strong { display: block; font-size: 18px; color: #333; }" +
        ".summary-item span { font-size: 14px; color: #666; }" +
        "table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }" +
        "th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }" +
        "th { background-color: #3b82f6; color: white; font-weight: bold; text-align: center; }" +
        "tr:nth-child(even) { background-color: #f9fafb; }" +
        ".text-right { text-align: right; }" +
        ".footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }" +
        "</style>" +
        "</head>" +
        "<body>" +
        "<div class=\"header\">" +
        "<h1>Loans Report - " + groupName + "</h1>" +
        "<h2>" + dateRangeStr + "</h2>" +
        "<p>Generated: " + generatedDate + "</p>" +
        "</div>" +
        "<div class=\"summary\">" +
        "<div class=\"summary-item\"><strong>" + stats.totalLoans + "</strong><span>Total Loans</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalDisbursed.toLocaleString() + " RWF</strong><span>Total Disbursed</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalRepaid.toLocaleString() + " RWF</strong><span>Total Repaid</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalOutstanding.toLocaleString() + " RWF</strong><span>Outstanding</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.uniqueMembers + "</strong><span>Borrowers</span></div>" +
        "</div>" +
        "<table>" +
        "<thead>" +
        "<tr><th>Date</th><th>Member</th><th>Group</th><th class=\"text-right\">Loan Amount</th><th class=\"text-right\">Interest</th><th class=\"text-right\">Total Payable</th><th class=\"text-right\">Paid</th><th class=\"text-right\">Outstanding</th><th>Status</th></tr>" +
        "</thead>" +
        "<tbody>" + tableRows + "</tbody>" +
        "</table>" +
        "<div class=\"footer\">" +
        "<p>Loans Report generated on " + generatedDate + " at " + generatedTime + "</p>" +
        "</div>" +
        "</body>" +
        "</html>";

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success("Print dialog opened");
        }, 250);
      };
    } catch (error: any) {
      toast.error("Failed to print: " + (error.message || "Unknown error"));
    }
  };

  const clearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedGroup("all");
    setSelectedBranch("all");
    setSelectedDistrict("all");
    setSelectedStatus("all");
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: any }> = {
      PENDING: { variant: "secondary", icon: Clock },
      APPROVED: { variant: "default", icon: CheckCircle },
      REJECTED: { variant: "destructive", icon: AlertCircle },
      COMPLETED: { variant: "outline", icon: CheckCircle },
    };

    const config = variants[status] || { variant: "secondary", icon: AlertCircle };
    const Icon = config.icon;

    const statusLabels: Record<string, string> = {
      PENDING: t("pages.loansReport.status.pending"),
      APPROVED: t("pages.loansReport.status.approved"),
      REJECTED: t("pages.loansReport.status.rejected"),
      COMPLETED: t("pages.loansReport.status.completed"),
    };

    return (
      <Badge variant={config.variant as any} className="gap-1">
        <Icon className="h-3 w-3" />
        {statusLabels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t("pages.loansReport.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("pages.loansReport.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToPDF}
            disabled={loans.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.pdf")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            disabled={loans.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.excel")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={loans.length === 0}
          >
            <Printer className="h-4 w-4 mr-1" />
            {t("common.print")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.loansReport.filters.title")}</CardTitle>
          <CardDescription>{t("pages.loansReport.filters.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.loansReport.filters.startDate")}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate?.from && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate?.from ? (
                      format(startDate.from, "MMM dd, yyyy")
                    ) : (
                      <span>{t("common.pickADate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DatePicker date={startDate} setDate={(date) => setStartDate(date as DateRange | undefined)} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.loansReport.filters.endDate")}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate?.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate?.to ? (
                      format(endDate.to, "MMM dd, yyyy")
                    ) : (
                      <span>{t("common.pickADate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DatePicker date={endDate} setDate={(date) => setEndDate(date as DateRange | undefined)} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.loansReport.filters.district")}</label>
              <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.loansReport.filters.allDistricts")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.loansReport.filters.allDistricts")}</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.loansReport.filters.branchSector")}</label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.loansReport.filters.allBranches")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.loansReport.filters.allBranches")}</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.name}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.loansReport.filters.group")}</label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.loansReport.filters.allGroups")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.loansReport.filters.allGroups")}</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id.toString()}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.loansReport.filters.status")}</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.loansReport.filters.allStatuses")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.loansReport.filters.allStatuses")}</SelectItem>
                  <SelectItem value="PENDING">{t("pages.loansReport.status.pending")}</SelectItem>
                  <SelectItem value="APPROVED">{t("pages.loansReport.status.approved")}</SelectItem>
                  <SelectItem value="REJECTED">{t("pages.loansReport.status.rejected")}</SelectItem>
                  <SelectItem value="COMPLETED">{t("pages.loansReport.status.completed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(startDate || endDate || selectedGroup !== "all" || selectedBranch !== "all" || selectedDistrict !== "all" || selectedStatus !== "all") && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                {t("pages.loansReport.filters.clearFilters")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.loansReport.stats.totalLoans")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalLoans}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.uniqueMembers === 1 
                ? t("pages.loansReport.stats.uniqueBorrowers", { count: stats.uniqueMembers })
                : t("pages.loansReport.stats.uniqueBorrowers_plural", { count: stats.uniqueMembers })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.loansReport.stats.totalDisbursed")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalDisbursed.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.loansReport.stats.loanPrincipal")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.loansReport.stats.totalRepaid")}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalRepaid.toLocaleString()} RWF
            </div>
            <p className="text-xs text-green-600 mt-1">{t("pages.loansReport.stats.repaymentRate", { rate: repaymentRate })}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.loansReport.stats.outstanding")}</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalOutstanding.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.loansReport.stats.yetToBeRepaid")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.loansReport.stats.loanStatus")}</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t("pages.loansReport.stats.pending")}:</span>
                <span className="font-medium">{stats.pendingLoans}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t("pages.loansReport.stats.approved")}:</span>
                <span className="font-medium">{stats.approvedLoans}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{t("pages.loansReport.stats.completed")}:</span>
                <span className="font-medium">{stats.completedLoans}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.loansReport.analytics.byStatus")}</CardTitle>
            <CardDescription>{t("pages.loansReport.analytics.byStatusDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(statusStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(statusStats)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .map(([status, data]) => (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(status)}
                        <span className="text-sm text-muted-foreground">
                          {data.count === 1 
                            ? t("pages.loansReport.analytics.loans", { count: data.count })
                            : t("pages.loansReport.analytics.loans_plural", { count: data.count })}
                        </span>
                      </div>
                      <div className="text-sm font-medium">
                        {Math.round(data.amount).toLocaleString()} RWF
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Borrowing Groups */}
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.loansReport.analytics.topBorrowingGroups")}</CardTitle>
            <CardDescription>{t("pages.loansReport.analytics.topBorrowingGroupsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(groupStats).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupStats)
                  .sort((a, b) => b[1].amount - a[1].amount)
                  .slice(0, 5)
                  .map(([group, data]) => {
                    const repaymentPct = data.amount > 0 ? ((data.repaid / data.amount) * 100).toFixed(0) : "0";
                    return (
                      <div key={group} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium truncate max-w-[200px]">
                            {group}
                          </span>
                          <span className="text-sm font-medium">
                            {Math.round(data.amount).toLocaleString()} RWF
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{data.count === 1 
                            ? t("pages.loansReport.analytics.loans", { count: data.count })
                            : t("pages.loansReport.analytics.loans_plural", { count: data.count })}</span>
                          <span className="text-green-600">{t("pages.loansReport.analytics.repaid", { percent: repaymentPct })}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Loans Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("pages.loansReport.table.title")}</CardTitle>
          <CardDescription>
            {loans.length === 1 
              ? t("pages.loansReport.table.description", { count: loans.length })
              : t("pages.loansReport.table.description_plural", { count: loans.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loansQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : loans.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("pages.loansReport.table.noLoans")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("pages.loansReport.table.adjustFilters")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.loansReport.table.date")}</TableHead>
                    <TableHead>{t("pages.loansReport.table.member")}</TableHead>
                    <TableHead>{t("pages.loansReport.table.group")}</TableHead>
                    <TableHead className="text-right">{t("pages.loansReport.table.loanAmount")}</TableHead>
                    <TableHead className="text-right">{t("pages.loansReport.table.interest")}</TableHead>
                    <TableHead className="text-right">{t("pages.loansReport.table.totalPayable")}</TableHead>
                    <TableHead className="text-right">{t("pages.loansReport.table.paid")}</TableHead>
                    <TableHead className="text-right">{t("pages.loansReport.table.outstanding")}</TableHead>
                    <TableHead>{t("pages.loansReport.table.status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map((loan) => (
                    <TableRow key={loan.id}>
                      <TableCell className="whitespace-nowrap">
                        {loan.createdAt ? format(new Date(loan.createdAt), "MMM dd, yyyy") : "—"}
                      </TableCell>
                      <TableCell>{loan.member?.fullNames || "—"}</TableCell>
                      <TableCell>{loan.group?.name || "—"}</TableCell>
                      <TableCell className="text-right">
                        {(Number(loan.amount) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {loan.interestRate || 0}%
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {(Number(loan.totalPayableAmount) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {(Number(loan.paidAmount) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        {(Number(loan.outstandingBalance) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(loan.status)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
